import React, { useState, useEffect } from 'react';
import { error } from '../utils/logger';

const CookieConsent = ({ onNavigate }) => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always true, cannot be disabled
    functional: true,
    analytics: true,
    marketing: false
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      setShowBanner(true);
    } else {
      // Load saved preferences
      try {
        const savedPreferences = JSON.parse(localStorage.getItem('cookie-preferences') || '{}');
        setPreferences(prev => ({ ...prev, ...savedPreferences }));
      } catch (err) {
        error('Error loading cookie preferences', { error: err.message, stack: err.stack });
      }
    }
  }, []);

  const acceptAll = () => {
    const allAccepted = {
      essential: true,
      functional: true,
      analytics: true,
      marketing: true
    };
    
    localStorage.setItem('cookie-consent', 'accepted');
    localStorage.setItem('cookie-preferences', JSON.stringify(allAccepted));
    setPreferences(allAccepted);
    setShowBanner(false);
    
    // Initialize analytics and other services based on preferences
    initializeServices(allAccepted);
  };

  const rejectNonEssential = () => {
    const essentialOnly = {
      essential: true,
      functional: false,
      analytics: false,
      marketing: false
    };
    
    localStorage.setItem('cookie-consent', 'declined');
    localStorage.setItem('cookie-preferences', JSON.stringify(essentialOnly));
    setPreferences(essentialOnly);
    setShowBanner(false);
    
    // Initialize only essential services
    initializeServices(essentialOnly);
  };

  const saveCustomPreferences = () => {
    localStorage.setItem('cookie-consent', 'custom');
    localStorage.setItem('cookie-preferences', JSON.stringify(preferences));
    setShowBanner(false);
    setShowDetails(false);
    
    // Initialize services based on custom preferences
    initializeServices(preferences);
  };

  const initializeServices = (prefs) => {
    // Initialize analytics if allowed
    if (prefs.analytics && window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    }
    
    // Initialize marketing cookies if allowed
    if (prefs.marketing && window.gtag) {
      window.gtag('consent', 'update', {
        'ad_storage': 'granted'
      });
    }
    
    // Dispatch custom event for other services to listen to
    window.dispatchEvent(new CustomEvent('cookiePreferencesUpdated', {
      detail: prefs
    }));
  };

  const handlePreferenceChange = (type, value) => {
    if (type === 'essential') return; // Essential cookies cannot be disabled
    
    setPreferences(prev => ({
      ...prev,
      [type]: value
    }));
  };

  if (!showBanner) return null;

  return (
    <>
      <div className="cookie-consent-banner">
        <div className="cookie-content">
          <div className="cookie-header">
            <h3>🍪 We use cookies</h3>
          </div>
          
          {!showDetails ? (
            <>
              <p className="cookie-description">
                We use cookies to enhance your experience, analyze usage, and provide personalized content. 
                By continuing to use our site, you agree to our use of cookies.
              </p>
              
              <div className="cookie-actions">
                <button onClick={acceptAll} className="btn-accept">
                  Accept All
                </button>
                <button onClick={rejectNonEssential} className="btn-decline">
                  Reject Non-Essential
                </button>
                <button 
                  onClick={() => setShowDetails(true)} 
                  className="btn-customize"
                >
                  Customize
                </button>
              </div>
              
              <div className="cookie-links">
                <button 
                  onClick={() => onNavigate && onNavigate('cookie-policy')} 
                  className="link-button"
                >
                  Cookie Policy
                </button>
                <span className="separator">•</span>
                <button 
                  onClick={() => onNavigate && onNavigate('privacy-policy')} 
                  className="link-button"
                >
                  Privacy Policy
                </button>
              </div>
            </>
          ) : (
            <div className="cookie-details">
              <h4>Customize Cookie Preferences</h4>
              <p className="cookie-details-description">
                Choose which types of cookies you want to allow. Essential cookies are required for the site to function.
              </p>
              
              <div className="cookie-categories">
                <div className="cookie-category">
                  <div className="category-header">
                    <label className="cookie-toggle">
                      <input
                        type="checkbox"
                        checked={preferences.essential}
                        disabled={true}
                        onChange={() => {}} // No-op since it's disabled
                      />
                      <span className="toggle-slider disabled"></span>
                      <span className="category-name">Essential Cookies</span>
                      <span className="required-badge">Required</span>
                    </label>
                  </div>
                  <p className="category-description">
                    Necessary for the website to function. Includes authentication, security, and basic functionality.
                  </p>
                </div>

                <div className="cookie-category">
                  <div className="category-header">
                    <label className="cookie-toggle">
                      <input
                        type="checkbox"
                        checked={preferences.functional}
                        onChange={(e) => handlePreferenceChange('functional', e.target.checked)}
                      />
                      <span className="toggle-slider"></span>
                      <span className="category-name">Functional Cookies</span>
                    </label>
                  </div>
                  <p className="category-description">
                    Remember your preferences and settings to provide a personalized experience.
                  </p>
                </div>

                <div className="cookie-category">
                  <div className="category-header">
                    <label className="cookie-toggle">
                      <input
                        type="checkbox"
                        checked={preferences.analytics}
                        onChange={(e) => handlePreferenceChange('analytics', e.target.checked)}
                      />
                      <span className="toggle-slider"></span>
                      <span className="category-name">Analytics Cookies</span>
                    </label>
                  </div>
                  <p className="category-description">
                    Help us understand how visitors interact with our website to improve our service.
                  </p>
                </div>

                <div className="cookie-category">
                  <div className="category-header">
                    <label className="cookie-toggle">
                      <input
                        type="checkbox"
                        checked={preferences.marketing}
                        onChange={(e) => handlePreferenceChange('marketing', e.target.checked)}
                      />
                      <span className="toggle-slider"></span>
                      <span className="category-name">Marketing Cookies</span>
                    </label>
                  </div>
                  <p className="category-description">
                    Used to deliver relevant advertisements and track advertising effectiveness.
                  </p>
                </div>
              </div>
              
              <div className="cookie-actions">
                <button onClick={saveCustomPreferences} className="btn-save">
                  Save Preferences
                </button>
                <button onClick={() => setShowDetails(false)} className="btn-back">
                  Back
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Overlay */}
      <div className="cookie-consent-overlay"></div>
    </>
  );
};

export default CookieConsent;
